/**
 * UI Store
 * Zustand store for UI-related state management.
 */

import { create } from 'zustand';
import type { ToastConfig } from '@types/common.types';
import { generateId } from '@utils/helpers';

/**
 * UI state interface.
 */
interface UIState {
  /** Sidebar collapsed state */
  sidebarCollapsed: boolean;
  /** Current theme */
  theme: 'light' | 'dark';
  /** Active toast notifications */
  toasts: ToastConfig[];
  /** Global loading overlay state */
  globalLoading: boolean;
  /** Modal open state */
  modalOpen: boolean;
  /** Current modal ID */
  activeModal: string | null;
}

/**
 * UI actions interface.
 */
interface UIActions {
  /** Toggle sidebar collapsed state */
  toggleSidebar: () => void;
  /** Set sidebar collapsed state */
  setSidebarCollapsed: (collapsed: boolean) => void;
  /** Toggle theme */
  toggleTheme: () => void;
  /** Set theme */
  setTheme: (theme: 'light' | 'dark') => void;
  /** Add a toast notification */
  addToast: (toast: Omit<ToastConfig, 'id'>) => void;
  /** Remove a toast notification by ID */
  removeToast: (id: string) => void;
  /** Clear all toasts */
  clearToasts: () => void;
  /** Set global loading state */
  setGlobalLoading: (loading: boolean) => void;
  /** Open a modal */
  openModal: (modalId: string) => void;
  /** Close the current modal */
  closeModal: () => void;
}

/**
 * Combined UI store type.
 */
type UIStore = UIState & UIActions;

/**
 * Initial state values.
 */
const initialState: UIState = {
  sidebarCollapsed: false,
  theme: 'light',
  toasts: [],
  globalLoading: false,
  modalOpen: false,
  activeModal: null,
};

/**
 * UI store for managing global UI state.
 */
export const useUIStore = create<UIStore>()((set) => ({
  ...initialState,

  toggleSidebar: () =>
    set((state) => ({
      sidebarCollapsed: !state.sidebarCollapsed,
    })),

  setSidebarCollapsed: (collapsed) =>
    set({
      sidebarCollapsed: collapsed,
    }),

  toggleTheme: () =>
    set((state) => ({
      theme: state.theme === 'light' ? 'dark' : 'light',
    })),

  setTheme: (theme) =>
    set({
      theme,
    }),

  addToast: (toast) =>
    set((state) => ({
      toasts: [
        ...state.toasts,
        {
          ...toast,
          id: generateId(),
        },
      ],
    })),

  removeToast: (id) =>
    set((state) => ({
      toasts: state.toasts.filter((t) => t.id !== id),
    })),

  clearToasts: () =>
    set({
      toasts: [],
    }),

  setGlobalLoading: (loading) =>
    set({
      globalLoading: loading,
    }),

  openModal: (modalId) =>
    set({
      modalOpen: true,
      activeModal: modalId,
    }),

  closeModal: () =>
    set({
      modalOpen: false,
      activeModal: null,
    }),
}));

/**
 * Selector hooks for common UI state values.
 */
export const useSidebarCollapsed = () => useUIStore((state) => state.sidebarCollapsed);
export const useTheme = () => useUIStore((state) => state.theme);
export const useToasts = () => useUIStore((state) => state.toasts);
export const useGlobalLoading = () => useUIStore((state) => state.globalLoading);

